import { Text, View } from '@react-pdf/renderer';

import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, gridStyles, reconciliationPageStyles } from '../styles.ts';
import { ReconciliationPageProps } from '../types.ts';

export const ReconciliationPage = ({
  subjectAddress,
  reconciliationCommentary,
  highestAndBestUseCommentary,
  alternativeComparablesCommentary,
  totalCostToCure,
  opinionOfMarketValue,
  revisionCommentary,
  isRevision,
  revisionType,
  commentaryV2,
  alerts,
  revisionData,
}: ReconciliationPageProps) => {
  const shouldDisplayAlertCommentarySection = alerts
    .filter(({ status }) => status === 'dismissed')
    .some(({ type }) => commentaryV2.alerts?.[type]);

  return (
    <>
      {isRevision && (
        <Tkv2PDFPage title="Reconciliation" address={subjectAddress}>
          <View style={reconciliationPageStyles.section}>
            <Text style={fontStyles.h5}>{revisionType} Commentary</Text>
            <Text style={fontStyles.body}>{revisionCommentary?.replace('\n', '\n\n')}</Text>
          </View>
          {alternativeComparablesCommentary?.map((commentary, index) => (
            <View key={index} style={reconciliationPageStyles.section}>
              <Text style={fontStyles.bodyBold}>
                Alternative Comparable {index + 1}: {revisionData?.comparables?.[index]?.address || 'N/A'}
              </Text>
              <Text style={fontStyles.body}>{commentary?.replace('\n', '\n\n')}</Text>
            </View>
          ))}
        </Tkv2PDFPage>
      )}
      <Tkv2PDFPage title="Reconciliation" address={subjectAddress}>
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Reconciliation Commentary</Text>
          <Text style={fontStyles.body}>{reconciliationCommentary?.replace('\n', '\n\n')}</Text>
        </View>
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.h5}>Highest & Best Use Commentary</Text>
          <Text style={fontStyles.body}>{highestAndBestUseCommentary?.replace('\n', '\n\n')}</Text>
        </View>
        <Text style={fontStyles.h5}>Opinion of Value</Text>
        <View
          style={{
            ...gridStyles.cardContainer,
            paddingHorizontal: 16,
            paddingVertical: 12,
            gap: 12,
          }}
        >
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Total Cost to Cure</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{totalCostToCure?.toString()}</Text>
            </View>
          </View>
          <View style={gridStyles.row}>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.h4}>Opinion of Market Value</Text>
            </View>
            <View style={gridStyles.gridCol}>
              <Text style={fontStyles.body}>{opinionOfMarketValue?.toString()}</Text>
            </View>
          </View>
        </View>
        <View style={reconciliationPageStyles.section}>
          <Text style={fontStyles.small}>
            The opinion of market value stated in this report is based on the defined scope of work,
            statement of assumptions and limiting conditions, and appraiser&apos;s certification.
          </Text>
        </View>
        {shouldDisplayAlertCommentarySection && (
          <View style={{ ...gridStyles.gridSection, marginRight: 20 }}>
            <Text style={fontStyles.h5}>Notes</Text>
            <List
              type="number"
              items={alerts
                .filter(({ status }) => status === 'dismissed')
                .map(({ type, message }) => (
                  <Text key={type} style={fontStyles.body}>
                    <Text style={fontStyles.bodyBold}>{message}&nbsp;</Text>
                    {commentaryV2.alerts?.[type]}
                  </Text>
                ))}
            />
          </View>
        )}
      </Tkv2PDFPage>
    </>
  );
};
